import { User, HelpCircle, LogOut, Mail, LayoutGrid, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/ui/count-badge";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SidebarTrigger, useSidebar } from "./ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentProfile } from "@/hooks/useCurrentProfile";
import { useToast } from "@/hooks/useToast";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useNavigate, useLocation } from "react-router-dom";
import { getUserInitials } from "@/lib/userUtils";
import { useTranslation } from "@/hooks/useTranslation";
import { useInboxUnreadCount } from "@/hooks/useInboxMessages";
import { useOrganization } from "@/contexts/OrganizationContext";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import { EntityImage } from "@/components/ui/entity-image";
import { cn } from "@/lib/utils";
import { useIsSuperAdmin } from "@/lib/permissions";
import { VoiceControlToggle } from "@/components/VoiceControlToggle";
import { BookkeeperHeaderToggle } from "@/components/bookkeeper/BookkeeperHeaderToggle";
import { useFeatureEnabled } from "@/hooks/useOrganizationFeatures";
import { useTheme } from "@/hooks/useThemePreset";
import { NotificationBell } from "@/components/notifications/NotificationBell";


export function UnifiedHeader() {
  const { user, signOut } = useAuth();
  const orgNavigate = useOrgNavigate();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const { data: unreadCount } = useInboxUnreadCount();
  const { selectedOrganization } = useOrganization();
  const { data: isSuperAdmin } = useIsSuperAdmin();
  const theme = useTheme();
  const { state: sidebarState } = useSidebar();
  const { enabled: voiceEnabled } = useFeatureEnabled('voice_control');

  const { data: profile } = useCurrentProfile();

  // Generate initials from display name or email
  const getInitials = () => {
    return getUserInitials({
      displayName: profile?.display_name,
      email: profile?.email || user?.email,
    });
  };

  const handleSignOut = async () => {
    try {
      const userEmail = profile?.email || user?.email || t('user');
      await signOut();
      toast.success(t('loggedOut', { email: userEmail }));
    } catch (err) {
      toast.error(t('logoutFailed'));
    }
  };

  return (
    <header className="w-full border-header-border bg-header text-header-foreground shrink-0 z-50">
      {/* pl-2 (not px-4) so the sidebar trigger's icon center lands on the
          primary rail's icon center line below it (8px + 32px/2 = 24px) */}
      <div className="flex h-12 items-center pl-2 pr-4 gap-4">
        {/* Left section: Toggle + Org switcher + Logo + Name */}
        <div className="flex items-center gap-2">
          {/* [&_svg]:size-5 matches the org-switcher LayoutGrid so the two
              header buttons read at one icon size (overrides the Button
              base's [&_svg]:size-4 via tailwind-merge) */}
          <SidebarTrigger className={cn("h-8 w-8 p-0 [&_svg]:size-5", sidebarState === "collapsed" && "bg-accent text-accent-foreground")} />
          <OrganizationSwitcher
            trigger={({ open }) => (
              <button
                type="button"
                aria-label="Switch organization"
                // pr-2 only (no left padding): the sibling gap-2 already
                // provides 8px after the sidebar trigger, keeping the gap
                // between the two icon boxes equal to the header's pl-2
                className="flex items-center gap-2 pr-2 py-1 cursor-pointer group rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className={cn(
                  "h-8 w-8 rounded-md flex items-center justify-center transition-colors group-hover:bg-accent group-hover:text-accent-foreground",
                  open && "bg-accent text-accent-foreground"
                )}>
                  <LayoutGrid className="h-5 w-5 shrink-0" />
                </div>
                {selectedOrganization?.logo_small ? (
                  <EntityImage
                    imagePath={selectedOrganization.logo_small}
                    alt={selectedOrganization.name}
                    className="flex-shrink-0 w-8 h-8"
                    variant="square"
                  />
                ) : (
                  <img
                    src={theme.logos.name}
                    alt="Releve"
                    className="flex-shrink-0 w-8 h-8 object-contain"
                  />
                )}
                {selectedOrganization && (
                  <div className="flex flex-col min-w-0 text-left">
                    <span className="text-sm font-medium truncate max-w-[200px]">
                      {selectedOrganization.name}
                    </span>
                    {selectedOrganization.description && (
                      <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {selectedOrganization.description}
                      </span>
                    )}
                  </div>
                )}
              </button>
            )}
          />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right section: User controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 relative"
            aria-label={t('inbox.title')}
            onClick={() => orgNavigate('/inbox')}
          >
            <Mail className="h-4 w-4" />
            {/* One header, one badge colour: the theme's header-badge pair,
                never `destructive` — unread is not an error (docs/THEMING.md) */}
            <CountBadge
              count={unreadCount}
              className="absolute -top-1 -right-1 bg-header-badge text-header-badge-foreground"
            />
          </Button>

          <NotificationBell />

          <Button
            variant="ghost"
            size="icon"
            className={cn("h-9 w-9", location.pathname.includes('/help') && "bg-accent text-accent-foreground")}
            onClick={() => orgNavigate('/help')}
          >
            <HelpCircle className="h-4 w-4" />
          </Button>

          {voiceEnabled && <VoiceControlToggle />}

          <BookkeeperHeaderToggle />

          <LanguageSwitcher />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full p-0">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            {/* Same frame as the org switcher's popover: p-0, the success
                accent border, a bordered identity row on top, p-1 item groups,
                and the sign-out in a bordered footer row. */}
            <DropdownMenuContent className="w-56 p-0 border-success" align="end" forceMount>
              <DropdownMenuLabel className="border-b px-3 py-2 font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none truncate">
                    {profile?.display_name || user?.email || t('user')}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground truncate">
                    {profile?.email || user?.email || t('noEmail')}
                  </p>
                </div>
              </DropdownMenuLabel>
              <div className="p-1">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t('profile')}</span>
                </DropdownMenuItem>
                {isSuperAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/system-settings')}>
                    <Shield className="mr-2 h-4 w-4" />
                    <span>{t('systemSettings')}</span>
                  </DropdownMenuItem>
                )}
              </div>
              <div className="border-t p-1">
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('logOut')}</span>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
